import { palette } from '../utils/palette';

// ─── Testimonials ─────────────────────────────────────────────────────────────

export type Testimonial = {
  name: string;
  quote: string;
  rating: number;
};

export const TESTIMONIALS: Testimonial[] = [
  {
    name: 'A. S.',
    quote:
      '"ColourSense just advised me to ditch the mustard yellow. Honestly it was about time someone said it."',
    rating: 5,
  },
  {
    name: 'Daisy F.',
    quote:
      '"What a massive upgrade from the old seasonal color guessing game!"',
    rating: 5,
  },
  {
    name: 'Joy W.',
    quote:
      '"I tried ColourSense and it\'s such a fun, eye-opening experience — in seconds, it showed me the colors that truly flatter my skin tone. Real-time, accurate, and super easy to use. Total confidence booster!"',
    rating: 5,
  },
  {
    name: 'Arianne P.',
    quote:
      '"This ColourSense helps you find colors that enhance your natural complexion and create a pleasing look. By understanding your undertone, you can easily select colors that complement your skin. It\'s 5 stars for me!"',
    rating: 5,
  },
  {
    name: 'Yoanna A.',
    quote:
      '"I love how it adjusts to my skin\'s changes in real time — so clever!"',
    rating: 5,
  },
  {
    name: 'Hannah J.',
    quote:
      '"It\'s like those color season tests but way smarter and genuinely helpful."',
    rating: 5,
  },
  {
    name: 'Angelica V.',
    quote:
      '"Glad to know people can use tools like this to find out their skin tone and colour recommendations from home. Excited to see tools like this help improve people\'s confidence and let them look their best without breaking the bank!"',
    rating: 4,
  },
  {
    name: 'S. F.',
    quote:
      '"Its standout feature is the integration of AI for precise color matching, targeting individuals who value data-driven style advice without the hassle of in-person consultations."',
    rating: 5,
  },
  {
    name: 'Anonymous',
    quote:
      '"I loved this website because it identifies my skin color and a color that suits my skin."',
    rating: 5,
  },
  {
    name: 'Rechel M.',
    quote: '"Super easy to use, fast and reliable. Highly recommended."',
    rating: 5,
  },
  {
    name: 'Stephanie T.',
    quote: '"Cool and fun to use but also helpful and accurate!"',
    rating: 5,
  },
  {
    name: 'Anonymous',
    quote:
      '"It is sooooo perfect as it gives recommendations for colours based on my skin tone — gonna use frequently!"',
    rating: 5,
  },
  {
    name: 'Anonymous',
    quote: '"Very nice, very demure, and very mindful."',
    rating: 5,
  },
];

// ─── How It Works ─────────────────────────────────────────────────────────────

export type HowItWorksStep = {
  step: string;
  icon: string;
  title: string;
  body: string;
  color: string;
};

export const HOW_IT_WORKS: HowItWorksStep[] = [
  {
    step: '01',
    icon: 'camera-outline',
    title: 'Upload Your Photo',
    body: 'Take or upload a selfie. Our AI reads your skin tone, hair colour, and eye colour in seconds — no filters, no fuss.',
    color: palette.softViolet,
  },
  {
    step: '02',
    icon: 'color-palette-outline',
    title: 'Get Your Colour Profile',
    body: 'Discover your seasonal palette — Spring, Summer, Autumn, or Winter — and the exact shades that make your features pop.',
    color: palette.cyanLimeGreen,
  },
  {
    step: '03',
    icon: 'sparkles-outline',
    title: 'Wear With Confidence',
    body: 'Try colours live with Virtual Drape, build palettes, and explore curated combinations — all tuned to your unique colouring.',
    color: palette.softPink,
  },
];

// ─── Seasons ──────────────────────────────────────────────────────────────────

export type SeasonDef = {
  name: string;
  label: string;
  description: string;
  gradient: [string, string];
  swatches: string[];
  textColor: string;
};

export const SEASONS: SeasonDef[] = [
  {
    name: 'Spring',
    label: 'Warm · Light · Clear',
    description:
      'Bright, warm, and fresh. Think peach, coral, and golden yellows that glow with warmth.',
    gradient: ['#FFDBB5', '#FFB347'],
    swatches: [
      '#FF9966',
      '#FFB347',
      '#FFC87A',
      '#F4A460',
      '#E8825A',
      '#FFCBA4',
    ],
    textColor: '#7A3B00',
  },
  {
    name: 'Summer',
    label: 'Cool · Soft · Muted',
    description:
      'Soft, cool, and romantic. Dusty rose, lavender, and powder blue are your signature.',
    gradient: ['#E8D5F0', '#C9A8E0'],
    swatches: [
      '#C9A8E0',
      '#B8A9C9',
      '#DDA0DD',
      '#BC8FBF',
      '#E6B8D0',
      '#A8B8D8',
    ],
    textColor: '#4A2060',
  },
  {
    name: 'Autumn',
    label: 'Warm · Deep · Muted',
    description:
      'Rich, earthy, and opulent. Burnt orange, olive, and terracotta are made for you.',
    gradient: ['#D4854A', '#8B4513'],
    swatches: [
      '#CC7722',
      '#8B4513',
      '#A0522D',
      '#CD853F',
      '#6B8E23',
      '#B8860B',
    ],
    textColor: '#3D1A00',
  },
  {
    name: 'Winter',
    label: 'Cool · Deep · Clear',
    description:
      'Bold, icy, and striking. True navy, crisp white, and jewel tones are your power palette.',
    gradient: ['#4A6FA5', '#1A2F6B'],
    swatches: [
      '#000080',
      '#4169E1',
      '#DC143C',
      '#008080',
      '#800080',
      '#C0C0C0',
    ],
    textColor: '#E8F0FF',
  },
];

// ─── Stats ────────────────────────────────────────────────────────────────────

export type StatDef = {
  value: string;
  label: string;
  icon: string;
};

export const STATS: StatDef[] = [
  { value: '10K+', label: 'Colour Analyses Done', icon: 'analytics-outline' },
  { value: '4.8★', label: 'Average User Rating', icon: 'star-outline' },
  { value: 'Free', label: 'Forever to Get Started', icon: 'gift-outline' },
  { value: '0', label: 'Sign-in Required', icon: 'shield-checkmark-outline' },
];

// ─── Pricing Tiers ────────────────────────────────────────────────────────────

export type PricingFeature = {
  text: string;
  included: boolean;
};

export type PricingTier = {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  badge?: string;
  color: string;
  description: string;
  features: PricingFeature[];
  cta: string;
  ctaVariant: 'outline' | 'primary';
};

export const PRICING_TIERS: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    annualPrice: 0,
    color: palette.cyanLimeGreen,
    description:
      'Everything you need to discover your colours — no card required.',
    features: [
      { text: '3 colour analyses per month', included: true },
      { text: 'Virtual Drape (core swatches)', included: true },
      { text: 'Colour Library — browse & copy', included: true },
      { text: 'Palette Generator (basic)', included: true },
      { text: 'Save & export palettes', included: false },
      { text: 'Analysis history', included: false },
      { text: 'Advanced AI model', included: false },
      { text: 'Early access to new features', included: false },
    ],
    cta: 'Get Started Free',
    ctaVariant: 'outline',
  },
  {
    id: 'plus',
    name: 'Plus',
    monthlyPrice: 4.99,
    annualPrice: 39,
    badge: 'Most Popular',
    color: palette.softViolet,
    description:
      'Unlimited power for anyone serious about their personal style.',
    features: [
      { text: 'Unlimited colour analyses', included: true },
      { text: 'Full Virtual Drape library', included: true },
      { text: 'Colour Library — browse & copy', included: true },
      { text: 'Palette Generator (advanced)', included: true },
      { text: 'Save & export palettes', included: true },
      { text: 'Analysis history', included: true },
      { text: 'Advanced AI model', included: false },
      { text: 'Early access to new features', included: false },
    ],
    cta: 'Start Plus',
    ctaVariant: 'primary',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 9.99,
    annualPrice: 79,
    badge: 'Beta Pricing',
    color: palette.softPink,
    description:
      'The full suite — plus every upcoming feature the moment it ships.',
    features: [
      { text: 'Everything in Plus', included: true },
      { text: 'Advanced AI model (higher accuracy)', included: true },
      { text: 'Early access to new features', included: true },
      { text: 'Smart Wardrobe (coming soon)', included: true },
      { text: 'Makeup Colour Matching (coming soon)', included: true },
      { text: 'Style Consultation access (coming soon)', included: true },
      { text: 'Downloadable PDF reports', included: true },
      { text: 'Priority support', included: true },
    ],
    cta: 'Go Pro',
    ctaVariant: 'primary',
  },
];

// ─── Products ─────────────────────────────────────────────────────────────────

export type ProductDef = {
  id: string;
  icon: string;
  title: string;
  tag?: string;
  subtitle: string;
  description: string;
  cta?: string;
  color: string;
  hoverColor?: string;
  features: string[];
  preview: string;
  available: boolean;
};

export const PRODUCTS: ProductDef[] = [
  {
    id: 'color-analysis',
    icon: 'color-palette-outline',
    title: 'Colour Analysis',
    tag: 'AI-Powered',
    subtitle: 'Your skin tone, season, and undertone — decoded.',
    description:
      'Upload a photo and get your complete colour profile in seconds. Know exactly which shades flatter you and which to skip — no guesswork, no stylist required.',
    cta: 'Analyse My Colours',
    color: palette.softViolet,
    hoverColor: palette.lightGrayViolet,
    features: [
      'Skin tone analysis',
      'Personal colour palette',
      'Colour recommendations',
      'Season classification',
    ],
    preview:
      'https://via.placeholder.com/300x600/8B5CF6/FFFFFF?text=Color+Analysis+Preview',
    available: true,
  },
  {
    id: 'virtual-drape',
    icon: 'shirt-outline',
    title: 'Virtual Drape Test',
    tag: 'Real-Time',
    subtitle: 'See colours on your face before you commit.',
    description:
      'Try virtual fabric swatches against your complexion in real time. Buy with total confidence — if it looks right here, it will look right in real life.',
    cta: 'Try the Drape Test',
    color: palette.cyanLimeGreen,
    hoverColor: palette.brightCyanLimeGreen,
    features: [
      'Real-time AR testing',
      'Camera integration',
      'Instant feedback',
      'Save favourites',
    ],
    preview:
      'https://via.placeholder.com/300x600/10B981/FFFFFF?text=Virtual+Drape+Preview',
    available: true,
  },
  {
    id: 'palette-generator',
    icon: 'albums-outline',
    title: 'Palette Generator',
    tag: 'Create',
    subtitle: 'Harmonious palettes for any mood or moment.',
    description:
      'Build a colour palette from scratch or from your season. Mix, match, and copy hex codes — ready for your wardrobe, interiors, or creative projects.',
    cta: 'Generate a Palette',
    color: palette.brightMatteRed,
    features: [
      'Custom palettes',
      'Colour harmony rules',
      'Export options',
      'Trend analysis',
    ],
    preview:
      'https://via.placeholder.com/300x600/EF4444/FFFFFF?text=Palette+Generator+Preview',
    available: true,
  },
  {
    id: 'explore-colours',
    icon: 'color-palette-outline',
    title: 'Colour Library',
    tag: 'Discover',
    subtitle: '50+ curated combinations to copy instantly.',
    description:
      'Browse hand-picked palettes spanning every season and aesthetic. Tap any swatch to copy its hex. No account, no friction — just beautiful colours.',
    cta: 'Browse Palettes',
    color: palette.softViolet,
    features: [
      'Curated palettes',
      'Copy hex codes',
      'Named combinations',
      'No sign-in required',
    ],
    preview:
      'https://via.placeholder.com/300x600/8B5CF6/FFFFFF?text=Explore+Colours',
    available: true,
  },
  {
    id: 'smart-wardrobe',
    icon: 'shirt-outline',
    title: 'Smart Wardrobe',
    subtitle: 'Organize and optimize your wardrobe',
    description:
      'Transform your closet into a smart, organized space. Get personalized outfit suggestions, track your style preferences, and discover new ways to wear what you already own.',
    color: palette.brightMatteRed,
    hoverColor: palette.softOrange,
    features: [
      'Wardrobe organization',
      'Outfit suggestions',
      'Style tracking',
      'Closet analytics',
    ],
    preview:
      'https://via.placeholder.com/300x600/EF4444/FFFFFF?text=Smart+Wardrobe+Preview',
    available: false,
  },
  {
    id: 'style-consultation',
    icon: 'people-outline',
    title: 'Style Consultation',
    subtitle: 'Expert Style Advice',
    description:
      'Get personalized style advice from professional color consultants and stylists',
    color: palette.softViolet,
    features: [
      'Expert consultations',
      'Personalized advice',
      'Style planning',
      'Follow-up sessions',
    ],
    preview:
      'https://via.placeholder.com/300x600/8B5CF6/FFFFFF?text=Style+Consultation+Preview',
    available: false,
  },
  {
    id: 'makeup-matching',
    icon: 'sparkles-outline',
    title: 'Makeup Colour Matching',
    subtitle: 'Perfect Makeup Colours',
    description:
      'Find the perfect makeup colours that complement your natural colouring',
    color: palette.softPink,
    features: [
      'Makeup recommendations',
      'Brand suggestions',
      'Shade matching',
      'Look creation',
    ],
    preview:
      'https://via.placeholder.com/300x600/EC4899/FFFFFF?text=Makeup+Matching+Preview',
    available: false,
  },
];

// ─── Bubble background ────────────────────────────────────────────────────────

export type BubbleDef = {
  size: number;
  pos: Record<string, string | number>;
  color: string;
  ty: number;
  s: [number, number, number];
  o: [number, number, number];
};

export const BUBBLE_DATA: BubbleDef[] = [
  {
    size: 100,
    pos: { top: '8%', left: '3%' },
    color: 'rgba(79,61,111,0.6)',
    ty: -50,
    s: [0.8, 1.2, 0.8],
    o: [0.4, 0.8, 0.4],
  },
  {
    size: 120,
    pos: { top: '20%', right: '3%' },
    color: 'rgba(148,117,225,0.6)',
    ty: -80,
    s: [0.6, 1.0, 0.6],
    o: [0.3, 0.7, 0.3],
  },
  {
    size: 80,
    pos: { top: '35%', left: '88%' },
    color: 'rgba(236,72,153,0.5)',
    ty: -60,
    s: [0.7, 1.1, 0.7],
    o: [0.25, 0.6, 0.25],
  },
  {
    size: 110,
    pos: { top: '75%', left: '5%' },
    color: 'rgba(59,130,246,0.5)',
    ty: -70,
    s: [0.5, 0.9, 0.5],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 95,
    pos: { top: '82%', right: '5%' },
    color: 'rgba(34,197,94,0.5)',
    ty: -40,
    s: [0.9, 1.3, 0.9],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 75,
    pos: { top: '12%', left: '50%' },
    color: 'rgba(245,158,11,0.5)',
    ty: -90,
    s: [0.8, 1.4, 0.8],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 85,
    pos: { top: '50%', left: '2%' },
    color: 'rgba(168,230,207,0.4)',
    ty: -75,
    s: [0.6, 1.1, 0.6],
    o: [0.25, 0.5, 0.25],
  },
  {
    size: 97,
    pos: { top: '60%', right: '2%' },
    color: 'rgba(255,182,193,0.5)',
    ty: -65,
    s: [0.7, 1.3, 0.7],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 82,
    pos: { top: '65%', right: '15%' },
    color: 'rgba(221,160,221,0.3)',
    ty: -85,
    s: [0.5, 1.0, 0.5],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 67,
    pos: { top: '45%', left: '2%' },
    color: 'rgba(152,251,152,0.4)',
    ty: -95,
    s: [0.9, 1.5, 0.9],
    o: [0.15, 0.4, 0.15],
  },
  {
    size: 90,
    pos: { top: '30%', left: '88%' },
    color: 'rgba(240,230,140,0.4)',
    ty: -55,
    s: [0.8, 1.2, 0.8],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 60,
    pos: { top: '70%', left: '80%' },
    color: 'rgba(255,160,122,0.3)',
    ty: -70,
    s: [0.6, 1.1, 0.6],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 82,
    pos: { top: '5%', left: '30%' },
    color: 'rgba(135,206,235,0.5)',
    ty: -80,
    s: [0.7, 1.3, 0.7],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 75,
    pos: { top: '55%', left: '25%' },
    color: 'rgba(255,20,147,0.4)',
    ty: -60,
    s: [0.5, 0.9, 0.5],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 88,
    pos: { top: '40%', right: '10%' },
    color: 'rgba(255,165,0,0.4)',
    ty: -100,
    s: [0.8, 1.4, 0.8],
    o: [0.15, 0.4, 0.15],
  },
  {
    size: 70,
    pos: { top: '25%', left: '15%' },
    color: 'rgba(138,43,226,0.4)',
    ty: -45,
    s: [0.7, 1.1, 0.7],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 92,
    pos: { top: '68%', left: '40%' },
    color: 'rgba(0,191,255,0.4)',
    ty: -65,
    s: [0.6, 1.0, 0.6],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 65,
    pos: { top: '15%', right: '20%' },
    color: 'rgba(255,105,180,0.4)',
    ty: -55,
    s: [0.8, 1.2, 0.8],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 78,
    pos: { top: '58%', right: '25%' },
    color: 'rgba(50,205,50,0.4)',
    ty: -75,
    s: [0.6, 1.1, 0.6],
    o: [0.2, 0.5, 0.2],
  },
  {
    size: 72,
    pos: { top: '32%', left: '60%' },
    color: 'rgba(255,140,0,0.4)',
    ty: -50,
    s: [0.7, 1.2, 0.7],
    o: [0.2, 0.5, 0.2],
  },
];

// ─── Splat particles ──────────────────────────────────────────────────────────

export const SPLAT_PARTICLES: Array<{
  rad: number;
  dist: number;
  size: number;
}> = [
  { angle: 12, dist: 88, size: 9 },
  { angle: 58, dist: 100, size: 7 },
  { angle: 103, dist: 78, size: 11 },
  { angle: 148, dist: 104, size: 6 },
  { angle: 192, dist: 90, size: 10 },
  { angle: 238, dist: 96, size: 7 },
  { angle: 282, dist: 82, size: 9 },
  { angle: 328, dist: 98, size: 8 },
].map(({ angle, dist, size }) => ({
  rad: (angle * Math.PI) / 180,
  dist,
  size,
}));

// ─── Float timing per bubble (delay ms, duration ms) ─────────────────────────

export const BUBBLE_FLOAT_TIMING: Array<{ delay: number; duration: number }> = [
  { delay: 0, duration: 3000 },
  { delay: 500, duration: 4000 },
  { delay: 1000, duration: 3500 },
  { delay: 1500, duration: 4500 },
  { delay: 2000, duration: 5000 },
  { delay: 2500, duration: 3200 },
  { delay: 3000, duration: 3800 },
  { delay: 3500, duration: 4200 },
  { delay: 4000, duration: 3600 },
  { delay: 4500, duration: 4800 },
  { delay: 5000, duration: 3400 },
  { delay: 5500, duration: 4600 },
  { delay: 6000, duration: 3900 },
  { delay: 6500, duration: 4300 },
  { delay: 7000, duration: 3700 },
  { delay: 7500, duration: 4200 },
  { delay: 8000, duration: 3600 },
  { delay: 8500, duration: 4400 },
  { delay: 9000, duration: 3800 },
  { delay: 9500, duration: 4000 },
];
